import { createCsvBlob, repairCsvText } from '../csvEncoding'

const pad = (n) => String(n).padStart(2, '0')
export const format2 = (value) => Number(value || 0).toFixed(2)

export const fileStamp = () => {
  const d = new Date()
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

export const downloadBlob = (blob, name) => {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

const stripHtml = (value) => repairCsvText(value).replace(/<[^>]*>/g, '')

export const csvEscape = (value) => `"${stripHtml(value).replace(/"/g, '""')}"`

const arrayBufferToBase64 = (buffer) => {
  let binary = ''
  const bytes = new Uint8Array(buffer)

  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }

  return window.btoa(binary)
}

const loadPdfFont = async (doc, url, fileName, fontName, fontStyle) => {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`PDF font not found: ${url}`)
  }

  const buffer = await response.arrayBuffer()
  const base64 = arrayBufferToBase64(buffer)

  doc.addFileToVFS(fileName, base64)
  doc.addFont(fileName, fontName, fontStyle)
}

export const ensureStatisticsPdfFonts = async (doc) => {
  await loadPdfFont(
    doc,
    '/fonts/Roboto-Regular.ttf',
    'Roboto-Regular.ttf',
    'Roboto',
    'normal'
  )

  await loadPdfFont(
    doc,
    '/fonts/Roboto-Bold.ttf',
    'Roboto-Bold.ttf',
    'Roboto',
    'bold'
  )
}

export const getStatsHeaders = (t) => [
  t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_TRIPS', { defaultValue: 'Počet jázd' }),
  t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_KM', { defaultValue: 'Najazdené km' }),
  t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_COST', { defaultValue: 'Celkové náklady' }),
  t('TRIPLOG.STATISTICS.SUMMARY.AVG_CONSUMPTION', { defaultValue: 'Spotreba' }),
  t('TRIPLOG.STATISTICS.SUMMARY.AVG_COST_PER_KM', { defaultValue: 'Priemerné náklady/km' }),
  t('TRIPLOG.STATISTICS.CO2.CO2_TOTAL', { defaultValue: 'Emisie CO₂' })
]

export const getVehicleHeaders = (t) => [
  t('TRIPLOG.STATISTICS.VEHICLES.VEHICLE_NAME', { defaultValue: 'Vozidlo' }),
  t('TRIPLOG.STATISTICS.VEHICLES.LICENSE_PLATE', { defaultValue: 'ŠPZ' }),
  t('TRIPLOG.STATISTICS.VEHICLES.KM', { defaultValue: 'km' }),
  t('TRIPLOG.STATISTICS.VEHICLES.COST', { defaultValue: 'Náklady' }),
  t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_FUEL', { defaultValue: 'Palivo' }),
  t('TRIPLOG.STATISTICS.VEHICLES.CONSUMPTION', { defaultValue: 'Spotreba' })
]

export const formatMonthExportLabel = (month, lang) => {
  const date = new Date(`${month}-01`)

  if (Number.isNaN(date.getTime())) {
    return month || ''
  }

  return `${date.toLocaleString(lang, {
    month: 'short'
  }).toUpperCase()} ${date.getFullYear()}`
}

export const hexToRgb = (hex) => {
  const normalized = String(hex || '')
    .replace('#', '')
    .trim()

  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return [16, 124, 107]
  }

  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16)
  ]
}

export const exportTableCSV = (
  block,
  t,
  filePrefix,
  title
) => {
  const titleRows = [
    [title],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    [
      t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
        defaultValue: 'Zobrazené vozidlo'
      }),
      block?.meta?.vehicle || ''
    ],
    []
  ]

  const csv = [
    ...titleRows,
    block?.headers || [],
    ...(block?.rows || [])
  ]
    .map((row) =>
      row.map(csvEscape).join(';')
    )
    .join('\n')

  downloadBlob(
    createCsvBlob(csv),
    `${filePrefix}_${fileStamp()}.csv`
  )
}

export const exportTableXLS = async (
  block,
  t,
  filePrefix,
  sheetName,
  title
) => {
  const XLSX = await import('xlsx-js-style')

  const titleRows = [
    [title],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    [
      t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
        defaultValue: 'Zobrazené vozidlo'
      }),
      block?.meta?.vehicle || ''
    ],
    []
  ]

  const headers = block?.headers || []
  const rows = block?.rows || []

  const worksheetData = [
    ...titleRows,
    headers,
    ...rows
  ]

  const ws = XLSX.utils.aoa_to_sheet(worksheetData)

  ws['!cols'] = headers.map((_, index) => ({
    wch: index === 0 ? 24 : 20
  }))

  titleRows.forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10,
          bold: true
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const headerRowIndex = titleRows.length

  headers.forEach((_, colIndex) => {
    const cellAddress = XLSX.utils.encode_cell({
      r: headerRowIndex,
      c: colIndex
    })

    if (!ws[cellAddress]) return

    ws[cellAddress].s = {
      font: {
        name: 'Arial',
        sz: 10,
        bold: true
      },
      fill: {
        fgColor: {
          rgb: 'F0F0F0'
        }
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  })

  rows.forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: headerRowIndex + 1 + rowIndex,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    sheetName
  )

  XLSX.writeFile(
    wb,
    `${filePrefix}_${fileStamp()}.xlsx`
  )
}

export const exportTablePDF = async (
  block,
  t,
  filePrefix,
  title
) => {
  const { jsPDF } = await import('jspdf')
  const autoTableModule = await import('jspdf-autotable')
  const autoTable =
    autoTableModule.default ||
    autoTableModule.autoTable

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  })

  await ensureStatisticsPdfFonts(doc)

  const marginLeft = 18
  const marginRight = 18
  const pageWidth = doc.internal.pageSize.getWidth()
  const usableWidth =
    pageWidth - marginLeft - marginRight

  const headers = block?.headers || []

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(14)
  doc.text(title, marginLeft, 28)

  doc.setFontSize(10)
  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
      defaultValue: 'Zobrazené obdobie'
    })}: ${block?.meta?.period || ''}`,
    marginLeft,
    44
  )

  doc.setFontSize(9)
  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
      defaultValue: 'Zobrazené vozidlo'
    })}: ${block?.meta?.vehicle || ''}`,
    marginLeft,
    58
  )

  autoTable(doc, {
    head: [headers],
    body: block?.rows || [],
    startY: 72,
    theme: 'grid',
    margin: {
      top: 18,
      right: marginRight,
      bottom: 18,
      left: marginLeft
    },
    tableWidth: usableWidth,
    styles: {
      font: 'Roboto',
      fontStyle: 'normal',
      fontSize: 8,
      cellPadding: 3,
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'left',
      lineWidth: 0.3,
      lineColor: [0, 0, 0]
    },
    headStyles: {
      font: 'Roboto',
      fontStyle: 'bold',
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineWidth: 0.4,
      lineColor: [0, 0, 0]
    },
    bodyStyles: {
      font: 'Roboto',
      fontStyle: 'normal',
      textColor: [0, 0, 0]
    }
  })

  downloadBlob(
    doc.output('blob'),
    `${filePrefix}_${fileStamp()}.pdf`
  )

}
